import React, { createContext, useContext, useReducer, useEffect } from 'react';

import {
    getConsent,
    setConsent,
    clearCategoryCookies,
    defaultConsent,
    CONSENT_DECISION
} from '~/utilities/consent';
import { loadGTM } from '~/utilities/gtm';
import { loadHubSpot } from '~/utilities/hubspot';

const ConsentContext = createContext();

const HYDRATE = 'HYDRATE';
const SAVE_PREFERENCES = 'SAVE_PREFERENCES';
const DECLINE_ALL = 'DECLINE_ALL';
const OPEN_SETTINGS = 'OPEN_SETTINGS';
const CLOSE_SETTINGS = 'CLOSE_SETTINGS';

const initialState = {
    ...defaultConsent,
    showModal: false,
    hydrated: false
};

const consentReducer = (state, action) => {
    const { type } = action;

    switch (type) {
        case HYDRATE:
            return {
                ...state,
                decision: action.decision,
                functional: action.functional,
                marketing: action.marketing,
                showModal: action.decision !== CONSENT_DECISION.SET,
                hydrated: true
            };
        case SAVE_PREFERENCES:
            return {
                ...state,
                decision: CONSENT_DECISION.SET,
                functional: action.functional,
                marketing: action.marketing,
                showModal: false
            };
        case DECLINE_ALL:
            return {
                ...state,
                decision: CONSENT_DECISION.SET,
                functional: false,
                marketing: false,
                showModal: false
            };
        case OPEN_SETTINGS:
            return { ...state, showModal: true };
        case CLOSE_SETTINGS:
            return { ...state, showModal: false };
        default:
            return state;
    }
};

export const ConsentProvider = ({ children }) => {
    const [state, dispatch] = useReducer(consentReducer, initialState);

    useEffect(() => {
        const { decision, functional, marketing } = getConsent();
        dispatch({ type: HYDRATE, decision, functional, marketing });
    }, []);

    useEffect(() => {
        if (state.marketing) {
            loadGTM(process.env.GATSBY_GOOGLE_TAG_MANAGER_ID);
            loadHubSpot(process.env.GATSBY_HUBSPOT_TRACKING_CODE);
        }
    }, [state.marketing]);

    // Neither GTM nor HubSpot can be unloaded once injected, so withdrawing
    // marketing consent mid-session only fully takes effect after a reload.
    const reloadIfMarketingWithdrawn = (nextMarketing) => {
        if (
            state.marketing &&
            !nextMarketing &&
            typeof window !== 'undefined'
        ) {
            window.location.reload();
        }
    };

    const savePreferences = ({ functional, marketing } = {}) => {
        const consent = setConsent({ functional, marketing });
        clearCategoryCookies(consent);
        dispatch({
            type: SAVE_PREFERENCES,
            functional: consent.functional,
            marketing: consent.marketing
        });
        reloadIfMarketingWithdrawn(consent.marketing);
    };

    const declineAll = () => {
        setConsent({ functional: false, marketing: false });
        clearCategoryCookies({ functional: false, marketing: false });
        dispatch({ type: DECLINE_ALL });
        reloadIfMarketingWithdrawn(false);
    };

    const openSettings = () => {
        dispatch({ type: OPEN_SETTINGS });
    };

    const closeSettings = () => {
        dispatch({ type: CLOSE_SETTINGS });
    };

    return (
        <ConsentContext.Provider
            value={{
                decision: state.decision,
                functional: state.functional,
                marketing: state.marketing,
                showModal: state.showModal,
                hydrated: state.hydrated,
                savePreferences,
                declineAll,
                openSettings,
                closeSettings
            }}
        >
            {children}
        </ConsentContext.Provider>
    );
};

export const useConsent = () => {
    const context = useContext(ConsentContext);

    if (context === undefined) {
        throw new Error('useConsent must be used within a ConsentProvider');
    }

    return context;
};
